import {
  useState,
  useEffect,
  useRef,
  ChangeEvent,
  KeyboardEvent,
  FocusEvent
} from "react";

import AutoCompleteResultList from "./AutoCompleteResultList";

import "./AutoCompleteInput.scss";

type OptionType = (string | null | undefined)[];

type AutoCompleteInputProps = {
  selectKey?: number;
  value?: string;
  options: OptionType[];
  onUserSelected: (index: number) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>, value: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
};

export default function AutoCompleteInput({
  selectKey = -1,
  value: propValue = "",
  options = [],
  onUserSelected,
  onChange,
  className,
  placeholder,
  disabled = false
}: AutoCompleteInputProps) {
  const [value, setValue] = useState<string | null | undefined>(propValue);
  const [selected, setSelected] = useState<number>(0);
  const [showOptions, setShowOptions] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement>(null);

  // Update local value if prop changes
  useEffect(() => {
    setValue(propValue);
  }, [propValue]);

  const selectItem = () => {
    const selectedValue = options[selected]?.[selectKey];
    onUserSelected(selected);
    setValue(selectedValue);
    setSelected(0);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    onChange?.(e, newValue);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (options.length === 0) {
      return;
    }
    if (e.key === "ArrowUp") {
      const newSelected = selected - 1 < 0 ? options.length - 1 : selected - 1;
      setSelected(newSelected);
      e.preventDefault();
    } else if (e.key === "ArrowDown") {
      const newSelected = selected + 1 >= options.length ? 0 : selected + 1;
      setSelected(newSelected);
    } else if (e.key === "Enter" || e.key === "Tab") {
      closeAndSelect();
    } else if (e.key === "Escape") {
      close();
    }
  };

  const handleItemSelected = (index: number) => {
    const selectedValue = options[index]?.[selectKey];
    onUserSelected(index);
    setValue(selectedValue);
    setSelected(0);
    setShowOptions(false);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setShowOptions(false);
    close();
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setShowOptions(true);
  };

  const close = () => {
    setSelected(0);
  };

  const closeAndSelect = () => {
    if (options.length > 0) {
      selectItem();
    }
    setSelected(0);
  };

  const safeSelectedIndex =
    selected >= options.length ? options.length - 1 : selected;

  return (
    <div className="autocompleteContainer">
      <input
        className={className}
        type="text"
        placeholder={placeholder}
        ref={inputRef}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        value={value ?? ""}
        disabled={disabled}
      />
      {options.length > 0 && showOptions && (
        <AutoCompleteResultList
          onItemSelected={handleItemSelected}
          options={options}
          selectedIndex={safeSelectedIndex}
        />
      )}
    </div>
  );
}
